import { ReactNode, useCallback } from 'react';

interface DashboardModalProps {
  /** Id for the <h2> title, referenced by aria-labelledby (e.g. "balance-modal-title"). */
  titleId: string;
  /** Material Symbols glyph name (e.g. "account_balance_wallet"). */
  icon: string;
  title: string;
  subtitle: string;
  onClose: () => void;
  /** Error message for the shared error row; null hides the row. */
  error: string | null;
  cancelLabel: string;
  /** Cancel-button action when it should differ from closing (e.g. "Back"). */
  onCancel?: () => void;
  submitLabel: string;
  /** Label shown on the submit button while isSubmitting. */
  submittingLabel: string;
  onSubmit: () => void;
  isSubmitting: boolean;
  submitDisabled: boolean;
  children: ReactNode;
}

export function DashboardModal({
  titleId,
  icon,
  title,
  subtitle,
  onClose,
  error,
  cancelLabel,
  onCancel,
  submitLabel,
  submittingLabel,
  onSubmit,
  isSubmitting,
  submitDisabled,
  children,
}: DashboardModalProps) {
  const stopPropagation = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation(),
    [],
  );

  return (
    <div
      className="balance-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={onClose}
    >
      <div className="balance-modal" onClick={stopPropagation}>
        <div className="balance-modal-handle" aria-hidden="true" />
        <div className="balance-modal-header">
          <div className="balance-modal-icon" aria-hidden="true">
            <span className="material-symbols-outlined">{icon}</span>
          </div>
          <div>
            <h2 id={titleId} className="balance-modal-title">
              {title}
            </h2>
            <div className="balance-modal-subtitle">{subtitle}</div>
          </div>
        </div>

        {children}

        {error != null && (
          <div className="balance-error" role="alert">
            <span className="material-symbols-outlined" aria-hidden="true">error</span>
            <span>{error}</span>
          </div>
        )}

        <div className="balance-modal-actions">
          <button
            type="button"
            className="balance-btn balance-btn-cancel"
            onClick={onCancel ?? onClose}
            disabled={isSubmitting}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="balance-btn balance-btn-save"
            onClick={onSubmit}
            disabled={submitDisabled || isSubmitting}
          >
            {isSubmitting ? submittingLabel : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
